import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Coins, Search, Plus, Settings2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';

interface UserWithCredits {
  id: string;
  email: string;
  full_name: string | null;
  credits_balance: number;
}

export const CreditManagementPanel = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserWithCredits[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserWithCredits | null>(null);
  const [newBalance, setNewBalance] = useState<number>(0);
  const [creditsToAdd, setCreditsToAdd] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auto-search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      handleUserSearch();
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm]);

  // Progressive search for users by email or name
  const handleUserSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const searchPattern = `%${term.toLowerCase()}%`;
      
      const { data: usersData, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name')
        .or(`email.ilike.${searchPattern},full_name.ilike.${searchPattern}`)
        .limit(10);

      if (userError) {
        console.error('Error searching users:', userError);
        toast.error('Error searching users');
        return;
      }

      if (!usersData || usersData.length === 0) {
        toast.error('No users found');
        setSearchResults([]);
        return;
      }

      // Fetch credit data for all found users
      const usersWithCredits = await Promise.all(
        usersData.map(async (user) => {
          const { data: creditData } = await supabase
            .from('user_credits')
            .select('credits_balance')
            .eq('user_id', user.id)
            .maybeSingle();

          return {
            ...user,
            credits_balance: creditData?.credits_balance ?? 0
          };
        })
      );

      setSearchResults(usersWithCredits);

      // Auto-select if only one result
      if (usersWithCredits.length === 1) {
        selectUser(usersWithCredits[0]);
        toast.success('User found');
      } else {
        toast.success(`Found ${usersWithCredits.length} users`);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Error searching users');
    } finally {
      setIsSearching(false);
    }
  };

  const selectUser = (user: UserWithCredits) => {
    setSelectedUser(user);
    setNewBalance(user.credits_balance);
    setSearchResults([]);
  };

  const handleSetBalance = async () => {
    if (!selectedUser || !reason) {
      toast.error('Please select a user and provide a reason');
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-manage-user-credits', {
        body: {
          action: 'set_balance',
          target_user_id: selectedUser.id,
          credits_amount: newBalance,
          reason
        }
      });

      if (error) throw error;

      toast.success(`Balance updated to ${newBalance} credits`);
      setSelectedUser({ ...selectedUser, credits_balance: newBalance });
      setReason('');
    } catch (error) {
      console.error('Error updating balance:', error);
      toast.error('Failed to update balance');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedUser || !reason || creditsToAdd <= 0) {
      toast.error('Please provide valid credits amount and reason');
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-manage-user-credits', {
        body: {
          action: 'add_credits',
          target_user_id: selectedUser.id,
          credits_amount: creditsToAdd,
          reason
        }
      });

      if (error) throw error;

      const newTotalBalance = selectedUser.credits_balance + creditsToAdd;
      toast.success(`Added ${creditsToAdd} credits. New balance: ${newTotalBalance}`);
      setSelectedUser({ ...selectedUser, credits_balance: newTotalBalance });
      setNewBalance(newTotalBalance);
      setCreditsToAdd(0);
      setReason('');
    } catch (error) {
      console.error('Error adding credits:', error);
      toast.error('Failed to add credits');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-yellow-500" />
          Credit Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User Search */}
        <div className="space-y-2">
          <Label htmlFor="search-term">Search User by Email or Name</Label>
          <div className="flex gap-2">
            <Input
              id="search-term"
              placeholder="Enter email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
            />
            <Button onClick={handleUserSearch} variant="outline" disabled={isSearching}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search Results (Multiple Users) */}
        {searchResults.length > 0 && (
          <Card className="border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Search Results ({searchResults.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className="flex justify-between items-center p-3 rounded-lg bg-secondary/50 hover:bg-secondary cursor-pointer transition-colors"
                  onClick={() => selectUser(user)}
                >
                  <div>
                    <p className="font-medium">{user.full_name || 'Unknown User'}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-yellow-600 flex items-center gap-1">
                      <Coins className="h-4 w-4" />
                      {user.credits_balance}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Selected User Info */}
        {selectedUser && (
          <div className="bg-primary/10 p-4 rounded-lg space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-lg">{selectedUser.full_name || 'Unknown User'}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                <p className="text-xs text-muted-foreground mt-1">ID: {selectedUser.id}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-yellow-600 flex items-center gap-1">
                  <Coins className="h-5 w-5" />
                  {selectedUser.credits_balance}
                </p>
                <p className="text-xs text-muted-foreground">Current Balance</p>
              </div>
            </div>
          </div>
        )}

        {/* Credit Operations */}
        {selectedUser && (
          <div className="space-y-6 pt-4 border-t">
            {/* Set Balance */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                Set Exact Balance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-balance">New Balance</Label>
                  <Input
                    id="new-balance"
                    type="number"
                    min="0"
                    value={newBalance}
                    onChange={(e) => setNewBalance(Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason-set">Reason</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger id="reason-set">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promotion">Promotional Campaign</SelectItem>
                      <SelectItem value="compensation">User Compensation</SelectItem>
                      <SelectItem value="correction">Error Correction</SelectItem>
                      <SelectItem value="marketing">Marketing Event</SelectItem>
                      <SelectItem value="manual">Manual Adjustment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                onClick={handleSetBalance} 
                disabled={isProcessing || !reason}
                className="w-full"
                variant="outline"
              >
                {isProcessing ? 'Updating...' : 'Set Balance'}
              </Button>
            </div>

            {/* Add Credits */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Credits (Bonus)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="credits-add">Credits to Add</Label>
                  <Input
                    id="credits-add"
                    type="number"
                    min="1"
                    value={creditsToAdd}
                    onChange={(e) => setCreditsToAdd(Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason-add">Reason</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger id="reason-add">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promotion">Promotional Campaign</SelectItem>
                      <SelectItem value="compensation">User Compensation</SelectItem>
                      <SelectItem value="marketing">Marketing Event</SelectItem>
                      <SelectItem value="bonus">Special Bonus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                onClick={handleAddCredits} 
                disabled={isProcessing || !reason || creditsToAdd <= 0}
                className="w-full bg-yellow-600 hover:bg-yellow-700"
              >
                {isProcessing ? 'Adding...' : `Add ${creditsToAdd} Credits`}
              </Button>
            </div>
          </div>
        )}

        {!selectedUser && (
          <div className="text-center py-8 text-muted-foreground">
            <Coins className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Search for a user to manage their credits</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};